import ApiConfig from '@api';
import DeviceUtils from '@shared/utils/device';
import IDevice from '@shared/types/interfaces/device';
import Storage from '@storage';
import checkLoginRequest from '@shared/api/login/checkLoginRequest';
import getVersionFromUserAgent from '../utils/getVersionFromUserAgent';
import loadSentry from '../../utils/sentry/loadSentry';
import { PlayerType } from '@shared/types/enums/player';
import { getAdvertisement, getVersion } from './functions';
import { store } from '@shared/store/store';
import {
  disableVisibility,
  enableVisibility,
  enableNetwork,
  disableNetwork,
  setDisableAnimations,
} from '@shared/store/slices/app.slice';

class DeviceConfigClass implements IDevice {
  advertisement: { ifaId: string; ifaType: string; limitAdTracking: boolean };
  capabilities: {
    balancer: boolean;
    exitFromMenu: boolean;
    magicRemote: boolean;
    disableAnimations: boolean;
    disableBlur: boolean;
    repeatSelect: boolean;
    disableAds: {
      prerolls: boolean;
      default: boolean;
    };
    image: {
      disableFitCover: boolean;
    };
    minimumCBCSVersion?: number;
    showCompatibilityModal?: boolean;
    supportUrl?: string;
  };
  info: {
    adsPlatform: string;
    analyticsPlatform: string;
    language: string;
    make: string;
    model: string;
    os: string;
    platform: string;
    uuid: string;
    version: number;
  };
  seller: { bundleId: string; storeUrl: string };
  sourcepoint?: {
    GDPR_OTT_ID?: number;
    GDPR_LEGACY_OTT_ID?: number;
    USP_OTT_ID?: number;
    USP_LEGACY_OTT_ID?: number;
  };
  defaultDashPlayer?: PlayerType | null;
  defaultHLSPlayer?: PlayerType | null;
  defaultFallbackPlayer?: PlayerType | null;
  getReferer?: () => string;
  exit: () => void;
  checkNetwork: () => boolean;

  constructor() {
    this.advertisement = {
      ifaId: '',
      ifaType: '',
      limitAdTracking: false,
    };

    this.capabilities = {
      balancer: false,
      magicRemote: false,
      exitFromMenu: false,
      disableAnimations: true,
      disableBlur: true,
      disableAds: {
        prerolls: false,
        default: false,
      },
      repeatSelect: false,
      image: {
        disableFitCover: true,
      },
      minimumCBCSVersion: 4.0,
      supportUrl: 'https://www.tivify.es/es/compatibilidad',
    };

    this.seller = {
      bundleId: 'G15147002586',
      storeUrl: 'https://www.samsung.com/us/appstore/app/G15147002586',
    };

    this.sourcepoint = {
      GDPR_OTT_ID: ApiConfig.environment === 'stage' ? 1222232 : 1222231,
    };

    // Device functions

    this.exit = () => tizen?.application?.getCurrentApplication()?.exit();
    this.checkNetwork = () => {
      const status = webapis.network.isConnectedToGateway();
      if (!status) return false;
      return true;
    };
    try {
      const smartMonitors = [
        'M80C',
        'M70C',
        'M50C',
        'G95SC',
        'G85NC',
        'G75NC',
        'S90PC',
        'G97NC',
        'M80B',
        'M70B',
        'M70BO',
        'M50B',
      ];
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.src = '$WEBAPIS/webapis/webapis.js';
      document.body.appendChild(s);

      const uuid =
        Storage.get('device') ||
        (webapis?.productinfo?.getDuid && webapis?.productinfo?.getDuid()) ||
        DeviceUtils.getUUID();

      // Get device info

      this.info = {
        language: window.navigator.language || '',
        os: 'Tizen',
        make: 'SAMSUNG',
        model:
          Storage.get('model') ||
          'samsung_' + webapis?.productinfo?.getModelCode() ||
          'samsung_' + uuid,
        platform: 'SMARTTV',
        analyticsPlatform: 'SAMSUNG',
        adsPlatform: 'samsung',
        uuid,
        version: getVersion() || -1,
      };

      if (this.info.version === 6 || this.info.version === 5.5) {
        this.defaultDashPlayer = PlayerType.AVPLAYER;
        this.defaultFallbackPlayer = PlayerType.AVPLAYER;
      }

      if (this.info.version > 3.0) {
        loadSentry();
      }
      const modelName = webapis.productinfo.getModel();

      // Update capabilities

      const lowVersion = this.info.version <= 7.0 && this.info.version !== -1;
      const disableBlurVersion =
        this.info.version <= 6.0 && this.info.version !== -1;

      this.capabilities = {
        ...this.capabilities,
        balancer:
          !smartMonitors.includes(modelName) && this.info.version >= 6.0,
        disableAds: {
          default: false,
          prerolls: this.info.version <= 3.0,
        },
        disableAnimations: lowVersion || smartMonitors.includes(modelName),
        disableBlur: disableBlurVersion || smartMonitors.includes(modelName),
        image: {
          disableFitCover: lowVersion,
        },
        minimumCBCSVersion: 4.0,
        showCompatibilityModal:
          this.info.version < 4.0 && this.info.version !== -1,
      };
      store.dispatch(setDisableAnimations(this.capabilities.disableAnimations));

      // Update storage

      Storage.set('device', this.info.uuid);
      Storage.set('model', this.info.model);

      // Update advertisement

      getAdvertisement().then((data) => {
        if (data) {
          this.advertisement = data;
        }
      });

      // Add logic to handle app in background state
      window.onload = () => {
        const callback = () => {
          if (document.hidden) {
            store.dispatch(disableVisibility());
            if (webapis.network.isConnectedToGateway()) {
              store.dispatch(enableNetwork());
            }
          } else {
            store.dispatch(enableVisibility());
          }
        };
        document.addEventListener('webkitvisibilitychange', callback);

        document.addEventListener('visibilitychange', callback);
      };

      webapis.network.addNetworkStateChangeListener(function (
        value: webapis.network.NetworkState
      ) {
        if (value == webapis.network.NetworkState.GATEWAY_DISCONNECTED) {
          store.dispatch(enableNetwork());
        } else if (value == webapis.network.NetworkState.GATEWAY_CONNECTED) {
          store.dispatch(disableNetwork());
        }
      });
    } catch (e) {
      this.info = {
        adsPlatform: 'SAMSUNG',
        analyticsPlatform: 'SMARTTV',
        language: window.navigator.language || '',
        os: 'Tizen',
        make: 'SAMSUNG',
        model: 'Unknown',
        platform: 'SMARTTV',
        uuid: DeviceUtils.getUUID(),
        version: getVersion() || -1,
      };
      console.error('Could not load tizen services');
    }

    // Check login logic

    setTimeout(() => {
      checkLoginRequest();
    });
    try {
      tizen.tvinputdevice.registerKeyBatch([
        'MediaRewind',
        'MediaFastForward',
        'MediaPlay',
        'MediaPause',
        'MediaPlayPause',
        'MediaTrackPrevious',
        'MediaTrackNext',
        'ChannelUp',
        'ChannelDown',
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
      ]);
    } catch (e) {
      console.error('cant register keys');
    }
  }

  getConnectionType = () => {
    try {
      const connectionType = webapis.network.getActiveConnectionType();
      return connectionType === 3 ? 'Ethernet' : 'Wi-Fi';
    } catch (e) {
      return 'Wi-Fi';
    }
  };
  getBrowser = () => {
    return 'Samsung Browser';
  };
  getBrowserVersion = () => {
    return `Samsung Browser ${getVersionFromUserAgent() || 'Unknown'}`;
  };
  getDeviceType = () => {
    return 'TV';
  };
  getDeviceModel = () => {
    return this.info.model;
  };
  getDeviceVendor = () => {
    return 'SAMSUNG';
  };
  getOs = () => {
    return 'Tizen';
  };
  getOsVersion = () => {
    return `Tizen ${this.info.version}`;
  };
}

const DeviceConfig = new DeviceConfigClass();

export default DeviceConfig;
